import React from 'react';
import { observer } from 'mobx-react-lite';
import {
  FaUndo, FaRedo, FaTextHeight, FaArrowsAlt,
  FaBold, FaItalic, FaUnderline, FaAlignLeft, FaAlignCenter, FaAlignRight,
  FaAlignJustify, FaTrash, FaArrowUp, FaArrowDown, FaAngleDoubleUp, FaAngleDoubleDown,
} from 'react-icons/fa';
import editorStore from '../../stores/EditorStore';
import { fabric } from 'fabric';

const FONTS = [
  'Arial', 'Helvetica', 'Georgia', 'Times New Roman',
  'Courier New', 'Verdana', 'Trebuchet MS', 'Impact',
  'Palatino', 'Garamond', 'Bookman', 'Comic Sans MS',
];

const C = {
  bg:      '#1c1e2e',
  border:  'rgba(255,255,255,0.07)',
  surface: '#252840',
  surface2:'#2f3252',
  text1:   '#f0f2ff',
  text2:   '#9da3c8',
  text3:   '#5c6290',
  accent:  '#5b79ff',
  accentSoft: 'rgba(91,121,255,0.15)',
  red:     '#ff5b6b',
};

const toolBtn = (active = false, danger = false): React.CSSProperties => ({
  width: 30, height: 30,
  borderRadius: 6,
  background: active ? C.accentSoft : 'none',
  border: 'none',
  color: active ? C.accent : C.text2,
  cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 12,
  flexShrink: 0,
  transition: 'all 150ms',
});

const sep: React.CSSProperties = {
  width: 1, height: 20, background: C.border, flexShrink: 0, margin: '0 4px',
};

// ─── Text format bar ─────────────────────────────────────────────────────────

const TextFormatBar = observer(() => {
  void editorStore.selectedElements;
  void editorStore.layersRevision;

  const canvas = editorStore.getCanvas();
  const activeObj = canvas?.getActiveObject() as fabric.Textbox | null;
  const isText = activeObj && (
    activeObj.type === 'textbox' || activeObj.type === 'i-text' || activeObj.type === 'text'
  );
  if (!isText || !activeObj) return null;

  const apply = (prop: string, value: any) => {
    (activeObj as any).set(prop, value);
    canvas?.requestRenderAll();
    editorStore.refreshAfterEdit();
  };

  const isBold      = (activeObj as any).fontWeight === 'bold';
  const isItalic    = (activeObj as any).fontStyle === 'italic';
  const isUnderline = !!(activeObj as any).underline;
  const fontSize: number  = (activeObj as any).fontSize ?? 24;
  const fontFamily: string = (activeObj as any).fontFamily ?? 'Arial';
  const textAlign: string  = (activeObj as any).textAlign ?? 'left';
  const fillColor: string  = String((activeObj as any).fill ?? '#000000');

  const inputStyle: React.CSSProperties = {
    background: C.surface2, border: `1px solid ${C.border}`, color: C.text1,
    fontSize: 11, borderRadius: 4, padding: '3px 6px', height: 26, outline: 'none',
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 3,
      background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: 20, padding: '3px 10px', marginLeft: 4,
    }}>
      <select style={{ ...inputStyle, width: 110 }} value={fontFamily}
        onChange={e => apply('fontFamily', e.target.value)}>
        {FONTS.map(f => <option key={f} value={f}>{f}</option>)}
      </select>

      <input type="number" min={6} max={400} style={{ ...inputStyle, width: 46, textAlign: 'center' }}
        value={fontSize} onChange={e => apply('fontSize', Number(e.target.value))} />

      <div style={sep} />

      <button style={toolBtn(isBold)} onClick={() => apply('fontWeight', isBold ? 'normal' : 'bold')} title="Bold">
        <FaBold size={11} />
      </button>
      <button style={toolBtn(isItalic)} onClick={() => apply('fontStyle', isItalic ? 'normal' : 'italic')} title="Italic">
        <FaItalic size={11} />
      </button>
      <button style={toolBtn(isUnderline)} onClick={() => apply('underline', !isUnderline)} title="Underline">
        <FaUnderline size={11} />
      </button>

      <div style={sep} />

      <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }} title="Text color">
        <span style={{ fontSize: 11, color: C.text2, fontWeight: 700 }}>A</span>
        <div style={{
          width: 22, height: 22, borderRadius: 4,
          border: `2px solid ${C.border}`, overflow: 'hidden', position: 'relative',
        }}>
          <input type="color" value={fillColor} onChange={e => apply('fill', e.target.value)}
            style={{ width: 30, height: 30, margin: '-4px', cursor: 'pointer', border: 'none', padding: 0 }} />
        </div>
      </label>

      <div style={sep} />

      {(['left','center','right','justify'] as const).map((a, i) => {
        const icons = [FaAlignLeft, FaAlignCenter, FaAlignRight, FaAlignJustify];
        const Icon = icons[i];
        return (
          <button key={a} style={toolBtn(textAlign === a)} onClick={() => apply('textAlign', a)} title={`Align ${a}`}>
            <Icon size={11} />
          </button>
        );
      })}
    </div>
  );
});

// ─── Object properties bar ────────────────────────────────────────────────────

const ObjectPropertiesBar = observer(() => {
  void editorStore.selectedElements;
  void editorStore.layersRevision;

  const canvas = editorStore.getCanvas();
  const activeObj = canvas?.getActiveObject();
  if (!activeObj) return null;

  const opacity = Math.round((activeObj.opacity ?? 1) * 100);
  const angle   = Math.round(activeObj.angle ?? 0);

  const set = (prop: string, value: any) => {
    (activeObj as any).set(prop, value);
    canvas?.requestRenderAll();
    editorStore.refreshAfterEdit();
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 4, paddingLeft: 8, borderLeft: `1px solid ${C.border}` }}>
      <span style={{ fontSize: 10, color: C.text3 }}>Opacity</span>
      <input type="range" min={0} max={100} value={opacity}
        style={{ width: 64, accentColor: C.accent }}
        onChange={e => set('opacity', Number(e.target.value) / 100)} />
      <span style={{ fontSize: 11, color: C.text2, minWidth: 28, textAlign: 'right' }}>{opacity}%</span>
      <div style={sep} />
      <span style={{ fontSize: 11, color: C.text3 }}>°</span>
      <input type="number" min={-360} max={360} value={angle}
        style={{
          width: 52, background: C.surface2, border: `1px solid ${C.border}`,
          color: C.text1, fontSize: 11, borderRadius: 4, padding: '3px 6px', height: 26, outline: 'none',
        }}
        onChange={e => set('angle', Number(e.target.value))} />
    </div>
  );
});

// ─── Layer controls ───────────────────────────────────────────────────────────

const LayerControls = observer(() => {
  if (editorStore.selectedElements.length === 0) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginLeft: 4, paddingLeft: 8, borderLeft: `1px solid ${C.border}` }}>
      <button style={toolBtn()} onClick={() => editorStore.bringToFront()} title="Bring to front"><FaAngleDoubleUp size={11} /></button>
      <button style={toolBtn()} onClick={() => editorStore.bringForward()} title="Bring forward"><FaArrowUp size={11} /></button>
      <button style={toolBtn()} onClick={() => editorStore.sendBackward()} title="Send backward"><FaArrowDown size={11} /></button>
      <button style={toolBtn()} onClick={() => editorStore.sendToBack()} title="Send to back"><FaAngleDoubleDown size={11} /></button>
      <button
        style={{ ...toolBtn(), color: C.red, marginLeft: 2 }}
        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,91,107,0.15)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
        onClick={() => editorStore.deleteSelected()} title="Delete"
      >
        <FaTrash size={11} />
      </button>
    </div>
  );
});

// ─── Main toolbar ─────────────────────────────────────────────────────────────

interface CanvasToolbarProps {
  onThumbnailClick: () => void;
  onPhotobookEditorClick: () => void;
  showTools: boolean;
}

const CanvasToolbar: React.FC<CanvasToolbarProps> = observer(({ onThumbnailClick, onPhotobookEditorClick, showTools }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    background: C.bg, borderBottom: `1px solid ${C.border}`,
    padding: '0 12px', minHeight: 48, gap: 4,
  }}>
    {/* Left: add + contextual controls */}
    <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' as const, flex: 1 }}>
      {/* Add Text */}
      <button
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '5px 12px', borderRadius: 8,
          background: C.accentSoft, border: `1px solid rgba(91,121,255,0.25)`,
          color: C.accent, fontSize: 12, fontWeight: 600, cursor: 'pointer',
        }}
        onClick={() => editorStore.activePage.addElement({ type: 'text', text: 'New Text', fontSize: 60, width: 400 })}
      >
        <FaTextHeight size={12} />
        Add Text
      </button>

      {/* Contextual format bar */}
      <TextFormatBar />
      <ObjectPropertiesBar />
      <LayerControls />

      {showTools && (
        <>
          <div style={sep} />
          <button
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 6, background: 'none', border: 'none', color: C.text2, fontSize: 12, cursor: 'pointer' }}
            onClick={onThumbnailClick}
          >
            Thumbnails
          </button>
          <button
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 6, background: 'none', border: 'none', color: C.text2, fontSize: 12, cursor: 'pointer' }}
            onClick={onPhotobookEditorClick}
          >
            Edit Page
          </button>
        </>
      )}
    </div>

    {/* Right: undo/redo + fullscreen */}
    <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
      <button style={toolBtn()} onClick={() => editorStore.history.undo()} title="Undo (Ctrl+Z)"><FaUndo size={12} /></button>
      <button style={toolBtn()} onClick={() => editorStore.history.redo()} title="Redo (Ctrl+Y)"><FaRedo size={12} /></button>
      <div style={sep} />
      <button
        style={toolBtn()}
        onClick={() => document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen()}
        title="Toggle fullscreen"
      >
        <FaArrowsAlt size={12} />
      </button>
    </div>
  </div>
));

export default CanvasToolbar;
